import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useBuilder } from '../lib/builderStore';
import type { Condition, Outcome } from '../lib/types';
import {
  Button, Card, Checkbox, Field, IconButton, Input, Select, Spinner, Textarea,
} from '../components/ui';

const OPERATORS = [
  { op: 'eq', label: 'is' },
  { op: 'ne', label: 'is not' },
  { op: 'gt', label: 'is greater than' },
  { op: 'gte', label: 'is at least' },
  { op: 'lt', label: 'is less than' },
  { op: 'lte', label: 'is at most' },
  { op: 'contains', label: 'contains' },
  { op: 'isAnswered', label: 'is answered' },
  { op: 'isEmpty', label: 'is empty' },
];

/**
 * The screens a respondent can land on when they finish.
 *
 * Outcomes have been in the schema since the beginning and editable only through the API,
 * so two flags the runtime honours — showing somebody their mark, and listing their answers
 * back — could be set by nobody using the product.
 *
 * The one rule that shapes this page: exactly one outcome has no condition, and it must be
 * last. It is the one that catches everybody the others did not, and a form without it
 * cannot publish. So it is not an outcome the author manages alongside the rest — it is
 * pinned to the bottom, cannot be deleted, and cannot be given a condition. Letting somebody
 * build an invalid arrangement and then explaining it at publish would be a worse version
 * of the same rule.
 */
export function Outcomes() {
  const { formId = '' } = useParams();

  const definition = useBuilder((s) => s.definition);
  const loadedFormId = useBuilder((s) => s.formId);
  const load = useBuilder((s) => s.load);
  const apply = useBuilder((s) => s.apply);
  const dirty = useBuilder((s) => s.dirty);
  const markSaved = useBuilder((s) => s.markSaved);

  const draft = useQuery({
    queryKey: ['definition', formId],
    queryFn: () => api.forms.getDefinition(formId),
    enabled: Boolean(formId),
  });

  useEffect(() => {
    if (draft.data && loadedFormId !== formId) load(formId, draft.data);
  }, [draft.data, formId, loadedFormId, load]);

  useEffect(() => {
    if (!dirty || !definition) return;
    const timer = setTimeout(async () => {
      await api.forms.saveDefinition(formId, definition);
      markSaved();
    }, 1200);
    return () => clearTimeout(timer);
  }, [dirty, definition, formId, markSaved]);

  if (!definition) return <div className="p-8"><Spinner /></div>;

  const questions = definition.pages.flatMap((page) =>
    page.elements
      .filter((e) => e.kind === 'question')
      .map((e) => ({ ref: e.ref, title: e.title ?? e.ref })),
  );

  const scored = definition.scoring?.enabled === true;

  // Split rather than filtered, because the two are edited differently and the order
  // between them is a rule rather than a preference.
  const conditional = definition.outcomes.filter((o) => o.when != null);
  const fallback = definition.outcomes.find((o) => o.when == null);

  const addOutcome = () => apply('Add outcome', (d) => {
    const first = questions[0];
    const next: Outcome = {
      ref: `outcome_${d.outcomes.length + 1}`,
      when: { op: 'eq', left: { ref: first?.ref ?? '' }, right: '' },
      title: 'Thanks',
      body: '',
      showScore: false,
      showAnswerReview: false,
      actions: [],
    };

    // Inserted before the fallback rather than appended: appending would put it after the
    // catch-all, where it could never be reached and would fail the publish check.
    const at = d.outcomes.findIndex((o) => o.when == null);
    if (at < 0) d.outcomes.push(next);
    else d.outcomes.splice(at, 0, next);
  });

  const addFallback = () => apply('Add final screen', (d) => {
    d.outcomes.push({
      ref: 'outcome_default',
      when: null,
      title: 'Thanks for your time.',
      body: '',
      showScore: false,
      showAnswerReview: false,
      actions: [],
    });
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold">Outcomes</h1>
          <p className="text-sm text-[var(--ff-text-muted)]">
            What somebody sees when they finish. The first outcome whose condition matches
            wins, so the order of these matters.
          </p>
        </div>
        <Button className="ml-auto whitespace-nowrap" variant="primary" onClick={addOutcome}>
          Add outcome
        </Button>
      </div>

      {conditional.map((outcome) => (
        <OutcomeCard
          key={outcome.ref}
          outcome={outcome}
          questions={questions}
          scored={scored}
          count={conditional.length}
        />
      ))}

      {fallback ? (
        <OutcomeCard
          key={fallback.ref}
          outcome={fallback}
          questions={questions}
          scored={scored}
          count={conditional.length}
          isFallback
        />
      ) : (
        // Publishing refuses a form with no catch-all, so this is offered here rather than
        // left as an error to meet later.
        <Card className="border-[var(--ff-danger)] p-4">
          <p className="text-sm">
            This form has no final screen. Somebody whose answers match none of the outcomes
            above would have nowhere to land, and the form cannot be published.
          </p>
          <Button className="mt-3" variant="primary" onClick={addFallback}>
            Add a final screen
          </Button>
        </Card>
      )}
    </div>
  );
}

function OutcomeCard({
  outcome, questions, scored, count, isFallback = false,
}: {
  outcome: Outcome;
  questions: { ref: string; title: string }[];
  scored: boolean;
  count: number;
  isFallback?: boolean;
}) {
  const apply = useBuilder((s) => s.apply);

  const edit = (label: string, mutate: (o: Outcome) => void) =>
    apply(label, (d) => {
      const target = d.outcomes.find((o) => o.ref === outcome.ref);
      if (target) mutate(target);
    });

  const move = (by: number) => apply('Reorder outcomes', (d) => {
    const from = d.outcomes.findIndex((o) => o.ref === outcome.ref);
    if (from < 0) return;

    const to = from + by;

    // Never past the catch-all: an outcome below it can never be reached.
    const limit = d.outcomes.findIndex((o) => o.when == null);
    if (to < 0 || (limit >= 0 && to >= limit)) return;

    const [moved] = d.outcomes.splice(from, 1);
    if (moved) d.outcomes.splice(to, 0, moved);
  });

  const condition: Condition = outcome.when ?? { op: 'eq', left: { ref: '' }, right: '' };
  const needsValue = !['isAnswered', 'isEmpty'].includes(condition.op);

  return (
    <Card className={isFallback ? 'border-dashed p-4' : 'p-4'}>
      <div className="mb-3 flex items-center gap-2">
        <code className="text-xs text-[var(--ff-text-muted)]">{outcome.ref}</code>

        {isFallback && (
          <span className="rounded bg-[var(--ff-surface-raised)] px-1.5 py-0.5 text-[11px]
                           text-[var(--ff-text-muted)]">
            Everyone else
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {!isFallback && count > 1 && (
            <>
              <IconButton label={`Move ${outcome.ref} up`} onClick={() => move(-1)}>↑</IconButton>
              <IconButton label={`Move ${outcome.ref} down`} onClick={() => move(1)}>↓</IconButton>
            </>
          )}

          {/* The catch-all has no delete: removing it makes the form unpublishable, and a
              control whose only outcome is an error is not a control. */}
          {!isFallback && (
            <IconButton
              label={`Delete ${outcome.ref}`}
              onClick={() => apply('Delete outcome', (d) => {
                const at = d.outcomes.findIndex((o) => o.ref === outcome.ref);
                if (at >= 0) d.outcomes.splice(at, 1);
              })}
            >
              ×
            </IconButton>
          )}
        </div>
      </div>

      {!isFallback && (
        <div className="mb-3 grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
          <Field label="When">
            <Select
              value={condition.left?.ref ?? ''}
              onChange={(e) => edit('Change outcome condition', (o) => {
                o.when = { ...condition, left: { ref: e.target.value } };
              })}
            >
              <option value="">Choose a question…</option>
              {questions.map((q) => (
                <option key={q.ref} value={q.ref}>{q.title}</option>
              ))}
            </Select>
          </Field>

          <Field label="Comparison">
            <Select
              value={condition.op}
              onChange={(e) => edit('Change outcome comparison', (o) => {
                o.when = { ...condition, op: e.target.value };
              })}
            >
              {OPERATORS.map((op) => (
                <option key={op.op} value={op.op}>{op.label}</option>
              ))}
            </Select>
          </Field>

          {needsValue ? (
            <Field label="Value">
              <Input
                value={String(condition.right ?? '')}
                onChange={(e) => edit('Change outcome value', (o) => {
                  o.when = { ...condition, right: e.target.value };
                })}
              />
            </Field>
          ) : <div />}
        </div>
      )}

      <Field label="Heading" required>
        <Input
          value={outcome.title}
          onChange={(e) => edit('Change outcome heading', (o) => { o.title = e.target.value; })}
        />
      </Field>

      <div className="mt-3">
        <Field
          label="Message"
          hint="Accepts basic HTML, and {{ref}} to include an answer."
        >
          <Textarea
            rows={3}
            value={outcome.body ?? ''}
            onChange={(e) => edit('Change outcome message', (o) => { o.body = e.target.value; })}
          />
        </Field>
      </div>

      <div className="mt-3 space-y-2">
        {/* Disabled rather than merely annotated when the form has no scoring. A box that
            can be ticked and then does nothing is the thing this whole page exists to stop
            happening elsewhere. */}
        <Checkbox
          label="Show their score"
          disabled={!scored}
          description={scored
            ? 'Only on this outcome. A form can show the mark to those who passed and not to those who did not.'
            : 'Turn on scoring in Settings first — there is no mark to show.'}
          checked={scored && (outcome.showScore ?? false)}
          onChange={(v) => edit('Toggle show score', (o) => { o.showScore = v; })}
        />

        <Checkbox
          label="List their answers back"
          description="Shown under the message. Answers classified as personal stay masked."
          checked={outcome.showAnswerReview ?? false}
          onChange={(v) => edit('Toggle answer review', (o) => { o.showAnswerReview = v; })}
        />
      </div>
    </Card>
  );
}
